// Regular expressions for detecting standard UUID/GUID formats
const LOOSE_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STORAGE_KEY = "ecommerce_slug_to_id_v1";

// In-memory lookup maps for instant synchronous resolution
const slugToIdMap = new Map();
const idToSlugMap = new Map();

/**
 * Hydrates the in-memory maps from sessionStorage
 */
function loadSessionMap() {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const obj = JSON.parse(raw);
      for (const [slug, id] of Object.entries(obj)) {
        if (slug && id) {
          slugToIdMap.set(slug, id);
          idToSlugMap.set(id, slug);
        }
      }
    }
  } catch {
    // Ignore storage parse issues
  }
}

/**
 * Persists the current mappings to sessionStorage
 */
function saveSessionMap() {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    const obj = {};
    for (const [slug, id] of slugToIdMap.entries()) {
      obj[slug] = id;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // Ignore storage write issues (quota/private browsing)
  }
}

// Initial hydrate
loadSessionMap();

/**
 * Checks if a string is a standard 36-character GUID/UUID
 */
export function isGuid(str) {
  if (!str || typeof str !== "string") return false;
  return LOOSE_UUID_REGEX.test(str.trim());
}

/**
 * Converts English text into a clean URL-friendly slug
 * e.g. "Minimalist Linen Shirt" -> "minimalist-linen-shirt"
 * e.g. "Classic Black & Gold Abaya" -> "classic-black-and-gold-abaya"
 */
export function slugify(text) {
  if (!text || typeof text !== "string") return "";
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/&/g, "-and-")
    .replace(/[\s\W-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Returns the SEO slug for a product, falling back to ID if name is missing.
 */
export function getProductSlug(product) {
  if (!product) return "";
  if (product.name && typeof product.name === "string") {
    const slug = slugify(product.name);
    if (slug) {
      if (product.id) {
        registerProductSlug(slug, product.id);
      }
      return slug;
    }
  }
  return product.id ? String(product.id) : "";
}

/**
 * Generates the storefront URL for a product
 */
export function getProductDetailUrl(product) {
  if (!product) return "/shop";
  const slug = getProductSlug(product);
  return slug ? `/product/${slug}` : "/shop";
}

/**
 * Registers a product or (slug, id) pair into memory & session storage
 */
export function registerProductSlug(slugOrProduct, possibleId) {
  if (!slugOrProduct) return;
  let slug = "";
  let id = "";

  if (typeof slugOrProduct === "object") {
    id = slugOrProduct.id;
    slug = slugify(slugOrProduct.name);
  } else {
    slug = slugOrProduct;
    id = possibleId;
  }

  if (slug && id) {
    slugToIdMap.set(slug, id);
    idToSlugMap.set(id, slug);
    saveSessionMap();
  }
}

/**
 * Registers an array of products
 */
export function registerProductList(products) {
  if (!Array.isArray(products)) return;
  for (const p of products) {
    if (p && p.id && p.name) {
      const slug = slugify(p.name);
      if (slug) {
        slugToIdMap.set(slug, p.id);
        idToSlugMap.set(p.id, slug);
      }
    }
  }
  saveSessionMap();
}

/**
 * Retrieves the cached product ID for a slug
 */
export function getCachedProductId(slug) {
  if (!slug) return null;
  const clean = slugify(slug);
  return slugToIdMap.get(clean) || null;
}

/**
 * Resolves a URL slug to its corresponding product ID:
 * 1. Returns immediately if already a GUID.
 * 2. Checks memory & session cache.
 * 3. Falls back to fetchProductsFn() to search catalog and populate cache.
 */
export async function resolveSlugToId(slug, fetchProductsFn) {
  if (!slug) return null;
  const trimmed = slug.trim();

  // 1. If it's already a GUID, return directly
  if (isGuid(trimmed)) return trimmed;

  const normalized = slugify(trimmed);

  // 2. Check cache
  const cached = getCachedProductId(normalized);
  if (cached) return cached;

  // 3. Fallback to querying product catalog
  if (typeof fetchProductsFn === "function") {
    try {
      const products = await fetchProductsFn();
      if (Array.isArray(products)) {
        registerProductList(products);

        // Exact match
        const exact = products.find((p) => slugify(p.name) === normalized);
        if (exact) return exact.id;

        // Fuzzy match (starts with or includes)
        const fuzzy = products.find((p) => {
          const s = slugify(p.name);
          return s.startsWith(normalized) || normalized.startsWith(s);
        });
        if (fuzzy) return fuzzy.id;
      }
    } catch (err) {
      console.error("Failed to resolve product slug via catalog API:", err);
    }
  }

  return null;
}
